import { useQuery } from '@tanstack/react-query';
import { shownState } from '../v2/ui/QueryStates';
import { useTRPC } from './trpc';

/**
 * The shop's timezone, and honestly about not having it yet.
 *
 * Six screens used to write `org.data?.timezone ?? 'America/New_York'` and never look at the
 * query's state. That reads as a harmless default and is not one:
 *
 *   - On every first paint the org row has not arrived, so a **Phoenix** shop's 8:00 start
 *     rendered as 5:00 — a real time, in the right format, three hours wrong, with nothing
 *     on screen to suggest it was provisional. It then silently corrected itself, which is
 *     worse: a number that changes while you read it is one nobody reports as a bug.
 *   - If `org.info` FAILS, the fallback is permanent. The shop runs the whole session on
 *     somebody else's clock and is never told.
 *
 * So there is no fallback. `tz` is `undefined` until it is known, and the type makes every
 * caller decide what to draw meanwhile — `format.ts` will not compile without one.
 *
 * ⭐ The state is `queryState`'s four-way union, NOT a pair of booleans, and that is
 * load-bearing on the surface this product exists for. Queries run `networkMode:
 * 'offlineFirst'` (`lib/query.ts`), so a query made offline with nothing cached is
 * **paused**: `isPending` stays true and `isError` stays false, forever. A first draft of
 * this hook read those two booleans by hand and would have hung the tech's stop screen on a
 * permanent skeleton — with no stops, no checklist and no retry — for a tech opening the app
 * in a basement on a device that had never cached the org row. `queryState`'s own docblock
 * warns about precisely that, having been written after the same bug hung the Finish button.
 */
export interface OrgClock {
  /** The org's IANA zone, or `undefined` while it is unknown. Never a guess. */
  tz: string | undefined;
  /**
   * `offline` — no signal and nothing cached; say so, do not spin.
   * `loading` — genuinely on its way.
   * `error` — could not be read; offer a retry.
   * `ready` — `tz` is a string.
   */
  state: 'offline' | 'loading' | 'error' | 'ready';
  refetch: () => void;
}

export function useOrgClock(): OrgClock {
  const trpc = useTRPC();
  const org = useQuery(trpc.org.info.queryOptions());
  const tz = org.data?.timezone;
  // `shownState`: the shop's timezone does not change, and a failed REFRESH of a zone we
  // already hold is no reason to replace a whole page with "Couldn't load the schedule".
  // Every screen behind this hook is full-page-gated on it, so it is the widest blanking
  // surface in the app (042).
  const state = shownState(org, org.data);
  return {
    tz,
    // `org.info` resolves to null for a session with no org — the query succeeded and there
    // is still no zone, which is an error to render, not a state to wait in.
    state: state === 'ready' && !tz ? 'error' : state,
    refetch: () => void org.refetch(),
  };
}
